import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useDispatch, useSelector } from 'react-redux';
import { fetchItems, postItem, putItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import { checkCiscoProductsLoaded } from '../../../support/slices/productsSlice';
import { CacheKey } from '../../../types/cache-keys';
import {
  AUTOMATION_ASSISTANCE_INVALID_CREATE_FIELDS,
  BU_REQUEST_TYPE_NAMES,
  BuRequestTypeName,
  getDefaultFieldValuesForRequestType,
  getInlineConfigDefaultsForProductRequestType,
  getRequestModeConfigForProductAndRequestType,
  getSupportedRequestTypeNamesForProduct,
  isBuRequestTypeName,
  JIRA_FIELD_DEFINITIONS,
} from './buJiraConstants';
import {
  EMPTY_SELECT_VALUE,
  getRelationId,
  hasSelectedId,
  parseSelectValue,
  RelationValue,
  SelectId,
  unpackItems,
} from './escalationFormUtils';

const isEmptySelectId = (value: SelectId): value is '' => value === EMPTY_SELECT_VALUE;

type Product = {
  id: number;
  name: string;
};

type EscalationTypeOption = {
  id: number;
  name: string;
};

type AssignmentTypeOption = {
  id: number;
  name: string;
  level: number;
};

export type EscalationBuRequestMapping = {
  id?: number;

  product?: RelationValue;
  product_name?: string | null;

  escalation_type?: RelationValue;
  escalation_type_name?: string | null;

  assignment_type?: RelationValue;
  assignment_type_name?: string | null;

  service_desk_id?: number | string | null;
  project_id?: number | string | null;
  project_key?: string | null;
  project_name?: string | null;

  request_type_id?: number | string | null;
  request_type_name?: string | null;
  request_create_key?: string | null;

  default_field_values?: Record<string, unknown> | null;
  is_active?: boolean;
  priority?: number | null;
  notes?: string | null;

  created?: string;
  last_updated?: string;
};

interface Props {
  open: boolean;
  editItem: EscalationBuRequestMapping | null;
  onClose: () => void;
  readOnly?: boolean;
}

const stringifyJson = (value?: Record<string, unknown> | null): string => {
  if (!value || typeof value !== 'object') return '{}';

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

const EscalationBuRequestMappingEditDialog: React.FC<Props> = ({
                                                                 open,
                                                                 editItem,
                                                                 onClose,
                                                                 readOnly = false,
                                                               }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const products = useSelector((state: RootState) => state.products.ciscoOnly);
  const isProductsLoading = useSelector(
    (state: RootState) => !!state.products.isCiscoProductsLoading
  );

  const [productId, setProductId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [escalationTypeId, setEscalationTypeId] = useState<SelectId>(EMPTY_SELECT_VALUE);
  const [assignmentTypeId, setAssignmentTypeId] = useState<SelectId>(EMPTY_SELECT_VALUE);

  const [serviceDeskId, setServiceDeskId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [projectName, setProjectName] = useState('');

  const [requestTypeId, setRequestTypeId] = useState('');
  const [requestTypeName, setRequestTypeName] = useState('');
  const [requestCreateKey, setRequestCreateKey] = useState('');

  const [defaultFieldValuesText, setDefaultFieldValuesText] = useState('{}');
  const [priority, setPriority] = useState<number>(100);
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [escalationTypes, setEscalationTypes] = useState<EscalationTypeOption[]>([]);
  const [assignmentTypes, setAssignmentTypes] = useState<AssignmentTypeOption[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    dispatch(checkCiscoProductsLoaded() as never);
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    Promise.all([
      fetchItems('db/escalation-type'),
      fetchItems('db/escalation-assignment-type'),
    ])
      .then(([typesResp, assignmentTypesResp]) => {
        setEscalationTypes(unpackItems<EscalationTypeOption>(typesResp));
        setAssignmentTypes(unpackItems<AssignmentTypeOption>(assignmentTypesResp));

        setProductId(getRelationId(editItem?.product));
        setEscalationTypeId(getRelationId(editItem?.escalation_type));
        setAssignmentTypeId(getRelationId(editItem?.assignment_type));

        setServiceDeskId(editItem?.service_desk_id != null ? String(editItem.service_desk_id) : '');
        setProjectId(editItem?.project_id != null ? String(editItem.project_id) : '');
        setProjectKey(editItem?.project_key ?? '');
        setProjectName(editItem?.project_name ?? '');

        setRequestTypeId(editItem?.request_type_id != null ? String(editItem.request_type_id) : '');
        setRequestTypeName(editItem?.request_type_name ?? '');
        setRequestCreateKey(editItem?.request_create_key ?? '');

        setDefaultFieldValuesText(stringifyJson(editItem?.default_field_values));
        setPriority(editItem?.priority ?? 100);
        setNotes(editItem?.notes ?? '');
        setIsActive(editItem?.is_active ?? true);
      })
      .finally(() => setLoading(false));
  }, [open, editItem]);

  const selectedAssignmentType = useMemo(
    () => assignmentTypes.find((type) => type.id === assignmentTypeId),
    [assignmentTypes, assignmentTypeId]
  );

  const supportedRequestTypeNames = useMemo(() => {
    if (!hasSelectedId(productId)) return BU_REQUEST_TYPE_NAMES;

    const supported = getSupportedRequestTypeNamesForProduct(productId);

    return supported.length > 0 ? supported : BU_REQUEST_TYPE_NAMES;
  }, [productId]);

  const requestTypeNameOptions = useMemo(() => {
    if (
      requestTypeName &&
      isBuRequestTypeName(requestTypeName) &&
      !supportedRequestTypeNames.includes(requestTypeName)
    ) {
      return [...supportedRequestTypeNames, requestTypeName];
    }

    return supportedRequestTypeNames;
  }, [supportedRequestTypeNames, requestTypeName]);

  const catalogConfig = useMemo(() => {
    if (!requestTypeName || !isBuRequestTypeName(requestTypeName)) return null;
    return getRequestModeConfigForProductAndRequestType(productId, requestTypeName);
  }, [productId, requestTypeName]);

  const applyCatalogDefaults = (nextProductId: SelectId, nextRequestTypeName: string) => {
    if (!nextRequestTypeName || !isBuRequestTypeName(nextRequestTypeName)) return;

    const nextCatalogConfig = getRequestModeConfigForProductAndRequestType(
      nextProductId,
      nextRequestTypeName
    );

    const inlineDefaults = getInlineConfigDefaultsForProductRequestType(
      nextProductId,
      nextRequestTypeName
    );

    setServiceDeskId(nextCatalogConfig?.service_desk_id || inlineDefaults.service_desk_id);
    setRequestTypeId(nextCatalogConfig?.request_type_id || inlineDefaults.request_type_id);

    if (!editItem?.id) {
      setDefaultFieldValuesText(
        JSON.stringify(getDefaultFieldValuesForRequestType(nextRequestTypeName), null, 2)
      );

      if (!notes.trim() && inlineDefaults.notes) {
        setNotes(inlineDefaults.notes);
      }
    }
  };

  const validationMessage = useMemo(() => {
    if (!hasSelectedId(productId)) return 'Product is required.';
    if (!hasSelectedId(assignmentTypeId)) return 'Assignment Type is required.';

    if (selectedAssignmentType && selectedAssignmentType.level !== 3) {
      return 'BU request mappings must use a Level 3 / Business Unit assignment type.';
    }

    if (!requestTypeName.trim()) return 'Request Type Name is required.';

    if (!isBuRequestTypeName(requestTypeName)) {
      return 'Request Type Name must be one of the supported BU request types.';
    }

    if (
      hasSelectedId(productId) &&
      supportedRequestTypeNames.length > 0 &&
      !supportedRequestTypeNames.includes(requestTypeName)
    ) {
      return `${requestTypeName} is not listed as supported for the selected product.`;
    }

    if (!serviceDeskId.trim()) return 'Service Desk ID is required.';
    if (!projectId.trim()) return 'Project ID is required.';
    if (!projectKey.trim()) return 'Project Key is required.';
    if (!projectName.trim()) return 'Project Name is required.';
    if (!requestTypeId.trim()) return 'Request Type ID is required.';

    try {
      JSON.parse(defaultFieldValuesText || '{}');
    } catch {
      return 'Default Field Values must be valid JSON.';
    }

    return '';
  }, [
    productId,
    assignmentTypeId,
    selectedAssignmentType,
    requestTypeName,
    supportedRequestTypeNames,
    serviceDeskId,
    projectId,
    projectKey,
    projectName,
    requestTypeId,
    defaultFieldValuesText,
  ]);

  const handleSave = async () => {
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    let defaultFieldValues: Record<string, unknown> = {};

    try {
      defaultFieldValues = JSON.parse(defaultFieldValuesText || '{}');
    } catch {
      alert('Default Field Values must be valid JSON.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        id: editItem?.id,

        product: productId,
        escalation_type: hasSelectedId(escalationTypeId) ? escalationTypeId : null,
        assignment_type: assignmentTypeId,

        service_desk_id: serviceDeskId,
        project_id: projectId,
        project_key: projectKey,
        project_name: projectName,

        request_type_id: requestTypeId,
        request_type_name: requestTypeName,
        request_create_key: requestCreateKey || null,

        default_field_values: defaultFieldValues,
        is_active: isActive,
        priority,
        notes: notes || null,
      };

      if (editItem?.id) {
        await putItem('db/escalation-bu-request-mapping', payload);
      } else {
        await postItem('db/escalation-bu-request-mapping', payload);
      }

      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationBuRequestMappings'] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'escalationProductSupport'] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.EscalationRequest] });

      onClose();
    } catch {
      alert('Failed to save BU request mapping.');
    } finally {
      setSaving(false);
    }
  };

  const isBusy = loading || saving || isProductsLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editItem?.id ? 'Edit BU Request Mapping' : 'Add BU Request Mapping'}
      </DialogTitle>

      <DialogContent>
        {isBusy && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          BU request mappings define which Jira Service Management request types are available
          for Level 3 / Business Unit escalation routing. Request type metadata is prefilled
          from the BU service catalog when possible.
        </Alert>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Product"
          value={productId}
          onChange={(e) => {
            const nextProductId = parseSelectValue(e.target.value);
            setProductId(nextProductId);

            const supported = getSupportedRequestTypeNamesForProduct(nextProductId);
            const nextRequestTypeName =
              requestTypeName && supported.includes(requestTypeName as BuRequestTypeName)
                ? requestTypeName
                : supported[0] || requestTypeName;

            if (nextRequestTypeName && nextRequestTypeName !== requestTypeName) {
              setRequestTypeName(nextRequestTypeName);
            }

            applyCatalogDefaults(nextProductId, nextRequestTypeName);
          }}
          disabled={readOnly || isBusy}
          required
        >
          {products.map((product: Product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Escalation Type"
          value={escalationTypeId}
          onChange={(e) => setEscalationTypeId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          helperText="Optional. Leave blank to make this a fallback mapping for any escalation type."
        >
          <MenuItem value="">
            <em>Any escalation type</em>
          </MenuItem>

          {escalationTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Assignment Type"
          value={assignmentTypeId}
          onChange={(e) => setAssignmentTypeId(parseSelectValue(e.target.value))}
          disabled={readOnly || isBusy}
          required
          helperText="Use the Level 3 / Business Unit assignment type."
        >
          {assignmentTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name} {type.level ? `(Level ${type.level})` : ''}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Request Type Name"
          value={requestTypeName}
          onChange={(e) => {
            const nextName = e.target.value;
            setRequestTypeName(nextName);
            applyCatalogDefaults(productId, nextName);
          }}
          disabled={readOnly || isBusy}
          required
          helperText={
            isEmptySelectId(productId)
              ? 'Select a product to filter request types by product support.'
              : 'Request types are filtered by the selected product service catalog.'
          }
        >
          {requestTypeNameOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        {catalogConfig?.request_url && (
          <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
            Jira create URL:{' '}
            <a href={catalogConfig.request_url} target="_blank" rel="noopener noreferrer">
              {catalogConfig.request_url}
            </a>
          </Alert>
        )}

        <TextField
          fullWidth
          margin="normal"
          label="Service Desk ID"
          value={serviceDeskId}
          onChange={(e) => setServiceDeskId(e.target.value)}
          disabled={readOnly || isBusy}
          required
          helperText={
            catalogConfig?.service_desk_id
              ? `Catalog default: ${catalogConfig.service_desk_id}`
              : undefined
          }
        />

        <TextField
          fullWidth
          margin="normal"
          label="Request Type ID"
          value={requestTypeId}
          onChange={(e) => setRequestTypeId(e.target.value)}
          disabled={readOnly || isBusy}
          required
          helperText={
            catalogConfig?.request_type_id
              ? `Catalog default: ${catalogConfig.request_type_id}`
              : undefined
          }
        />

        <TextField
          fullWidth
          margin="normal"
          label="Request Type Category"
          value={catalogConfig?.request_type_category || ''}
          disabled
          helperText="Derived from the BU service catalog."
        />

        <TextField
          fullWidth
          margin="normal"
          label="Project ID"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={readOnly || isBusy}
          required
          helperText="Required by local tracking. Fill from Jira project metadata."
        />

        <TextField
          fullWidth
          margin="normal"
          label="Project Key"
          value={projectKey}
          onChange={(e) => setProjectKey(e.target.value)}
          disabled={readOnly || isBusy}
          required
          helperText="Required by local tracking. Example: project key used by Jira."
        />

        <TextField
          fullWidth
          margin="normal"
          label="Project Name"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          disabled={readOnly || isBusy}
          required
          helperText="Required by local tracking. Example: Jira project display name."
        />

        <TextField
          fullWidth
          margin="normal"
          label="Request Create Key"
          value={requestCreateKey}
          onChange={(e) => setRequestCreateKey(e.target.value)}
          disabled={readOnly || isBusy}
          helperText="Optional create key used by the Jira integration."
        />

        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          Default Field Values can use raw Jira field IDs or backend-supported friendly aliases.
          Common values include Business Impact <strong>customfield_13500</strong> and Your Role{' '}
          <strong>customfield_12100</strong>.
        </Alert>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {JIRA_FIELD_DEFINITIONS.map((field) => (
            <Chip
              key={field.fieldId}
              size="small"
              label={`${field.label}: ${field.fieldId}`}
              variant="outlined"
            />
          ))}
        </Box>

        {requestTypeName === 'Automation Assistance' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Automation Assistance create requests should not include{' '}
            {AUTOMATION_ASSISTANCE_INVALID_CREATE_FIELDS.join(', ')} in requestFieldValues.
            The backend filters these fields before Jira create.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={readOnly || isBusy || !requestTypeName}
            onClick={() =>
              setDefaultFieldValuesText(
                JSON.stringify(getDefaultFieldValuesForRequestType(requestTypeName), null, 2)
              )
            }
          >
            Use Default Field Values
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={readOnly || isBusy}
            onClick={() =>
              setDefaultFieldValuesText(
                JSON.stringify(
                  {
                    business_impact: '14301',
                    your_role: '12800',
                  },
                  null,
                  2
                )
              )
            }
          >
            Use Friendly Aliases
          </Button>

          <Button
            size="small"
            variant="outlined"
            disabled={readOnly || isBusy}
            onClick={() =>
              setDefaultFieldValuesText(
                JSON.stringify(
                  {
                    customfield_13500: '14301',
                    customfield_12100: '12800',
                  },
                  null,
                  2
                )
              )
            }
          >
            Use Raw Field IDs
          </Button>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={5}
          margin="normal"
          label="Default Field Values JSON"
          value={defaultFieldValuesText}
          onChange={(e) => setDefaultFieldValuesText(e.target.value)}
          disabled={readOnly || isBusy}
          helperText='Example: { "business_impact": "14301", "your_role": "12800" }'
        />

        <TextField
          fullWidth
          margin="normal"
          label="Priority"
          type="number"
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value))}
          disabled={readOnly || isBusy}
          helperText="Lower numbers are preferred first."
        />

        <TextField
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={readOnly || isBusy}
        />

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={readOnly || isBusy}
            />
          }
          label="Active"
        />

        {!!validationMessage && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {validationMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>

        {!readOnly && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isBusy || !!validationMessage}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EscalationBuRequestMappingEditDialog;